import React, { useState, useEffect, useRef } from 'react';
import Quote from '../components/Quote';
import { SiteContent } from '../content';

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', interest: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      if (el.getBoundingClientRect().top >= window.innerHeight) {
        el.classList.add('below-fold');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!validateEmail(formData.email)) errs.email = 'Please enter a valid email';
    if (!formData.message.trim()) errs.message = 'Message is required';
    return errs;
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(validate());
  };

  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    setTouched({ name: true, email: true, message: true });
    if (Object.keys(errs).length > 0) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: 'da004216-b87f-4731-8e9e-3e683cdc367e',
          subject: `ERB Contact: ${formData.interest || 'General Inquiry'}`,
          from_name: formData.name,
          name: formData.name,
          email: formData.email,
          interest: formData.interest,
          message: formData.message,
        }),
      });

      const result = await response.json();
      if (!result.success) throw new Error('Submission failed');

      setSubmitted(true);
      setFormData({ name: '', email: '', interest: '', message: '' });
      setTouched({});
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { hero, invitation, form, closing } = SiteContent.contact;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper">
      {/* SECTION 1: HERO */}
      <section className="pt-40 md:pt-60 pb-12 text-center px-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-on-scroll animate-fade-up">
            <h1 className="section-title text-5xl md:text-7xl lg:text-8xl mb-8 leading-none">{hero.title}</h1>
          </div>
          <div className="animate-on-scroll animate-fade-up delay-200">
            <p className="text-xl md:text-2xl text-black/60 italic font-medium">{hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* SECTION 2 & 3: INVITATION & FORM */}
      <section className="pb-32 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-rich-black p-8 md:p-16 rounded-2xl shadow-2xl relative border-t-8 border-[#A65D3F] animate-on-scroll animate-scale-in">
            <p className="text-lg md:text-xl text-[#F5F0E6] mb-12 italic border-l-2 border-[#A65D3F]/60 pl-6 leading-relaxed">
              {invitation}
            </p>

            {submitted ? (
              <div className="text-center py-20 animate-fade-in">
                <div className="w-16 h-16 bg-[#A65D3F] rounded-full flex items-center justify-center mx-auto mb-8">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="section-title text-3xl md:text-4xl text-white mb-4">{form.success.title}</h3>
                <p className="text-white/80 tracking-widest text-xs uppercase font-black mb-2">{form.success.desc}</p>
                <p className="text-white/60 text-sm">We'll respond within 24 hours.</p>
                <div className="pt-12">
                  <button onClick={() => setSubmitted(false)} className="text-xs font-black tracking-widest uppercase border-b border-[#A65D3F] text-[#A65D3F] pb-1 py-3 px-2 hover:text-white transition-colors">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-10" noValidate>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="flex flex-col">
                    <label className="text-xs font-black tracking-widest uppercase text-white/70 mb-3">Name</label>
                    <input
                      type="text"
                      placeholder="YOUR NAME"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      onBlur={() => handleBlur('name')}
                      className={`bg-white/5 border-b p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest uppercase ${touched.name && errors.name ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {touched.name && errors.name && <span className="text-red-400 text-xs mt-2 font-medium">{errors.name}</span>}
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-black tracking-widest uppercase text-white/70 mb-3">Email</label>
                    <input
                      type="email"
                      placeholder="YOUR EMAIL"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      onBlur={() => handleBlur('email')}
                      className={`bg-white/5 border-b p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest uppercase ${touched.email && errors.email ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {touched.email && errors.email && <span className="text-red-400 text-xs mt-2 font-medium">{errors.email}</span>}
                  </div>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-black tracking-widest uppercase text-white/70 mb-3">I'm interested in...</label>
                  <select
                    value={formData.interest}
                    onChange={(e) => setFormData(prev => ({ ...prev, interest: e.target.value }))}
                    className="w-full bg-white/5 border-b border-white/20 p-4 text-white outline-none cursor-pointer font-condensed tracking-widest uppercase appearance-none focus:border-[#A65D3F] transition-all"
                  >
                    {form.options.map((opt, i) => (
                      <option key={i} value={opt.value} className="text-black">{opt.label.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-black tracking-widest uppercase text-white/70 mb-3">Your Message</label>
                  <textarea
                    rows={4}
                    placeholder="HOW SHALL WE REGENERATE?"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    onBlur={() => handleBlur('message')}
                    className={`bg-white/5 border-b p-4 text-white outline-none focus:border-[#A65D3F] transition-all font-condensed tracking-widest resize-none ${touched.message && errors.message ? 'border-red-400' : 'border-white/20'}`}
                  />
                  {touched.message && errors.message && <span className="text-red-400 text-xs mt-2 font-medium">{errors.message}</span>}
                </div>
                {submitError && (
                  <p className="text-red-400 text-sm text-center font-medium">{submitError}</p>
                )}
                <div className="pt-4">
                  <button type="submit" disabled={isSubmitting} className="w-full btn-primary py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? 'SUBMITTING...' : 'PLANT THIS MESSAGE'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 5: CLOSING (Dark Background) */}
      <section className="section-padding bg-rich-black text-center relative overflow-hidden">
        <img
          src="/images/home-hero.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 rotate-180"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <Quote large text={closing} className="drop-shadow-sm" />
          <div className="mt-20 opacity-20">
            <div className="w-px h-20 bg-white mx-auto"></div>
            <p className="text-white text-xs tracking-[0.5em] mt-4 uppercase">Beneath the surface</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;